// 订阅新价格体系：价区/站点静态映射（对齐 perseus shared/subscription-zone 口径）。
// 「方案」= 价区（PriceZone 四值闭集）的产品化叫法；展示名走 i18n 中文 key，
// 不消费 wire 的 priceZones[].name（后端直出中文，违反 i18n 兜底）。
// 站点集合镜像后端 PriceZoneService（后端不返回站点清单，前端静态枚举）。

/** 价区四值闭集 */
export const PRICE_ZONE_CODES = ['CN', 'SEA', 'LATAM', 'US'] as const;
export type PriceZoneCode = (typeof PRICE_ZONE_CODES)[number];

/** wire 价区 code → 闭集值（未知价区归 undefined，不整块崩） */
export function narrowPriceZone(value: string | null | undefined): PriceZoneCode | undefined {
  return value !== null &&
    value !== undefined &&
    (PRICE_ZONE_CODES as readonly string[]).includes(value)
    ? (value as PriceZoneCode)
    : undefined;
}

/** 价区展示名（产品化命名：跨境/东南亚/拉美/国际） */
export const PRICE_ZONE_LABELS: Record<PriceZoneCode, string> = {
  CN: '跨境',
  SEA: '东南亚',
  LATAM: '拉美',
  US: '国际',
};

/** 价区 → 可授权站点集合（ISO alpha-2，展示用；授权限制以 auth/scope 为准） */
export const ZONE_STORE_REGIONS: Record<PriceZoneCode, readonly string[]> = {
  CN: ['CN'],
  SEA: ['ID', 'TH', 'VN', 'PH', 'MY', 'SG'],
  LATAM: [
    'BR',
    'MX',
    'AR',
    'CO',
    'CL',
    'BO',
    'CR',
    'DO',
    'EC',
    'GT',
    'HN',
    'NI',
    'PA',
    'PY',
    'PE',
    'SV',
    'UY',
    'VE',
  ],
  US: [
    'US',
    'CA',
    'JP',
    'PL',
    'ES',
    'FR',
    'UK',
    'GB',
    'RU',
    'DE',
    'IT',
    'IE',
    'NL',
    'BE',
    'CZ',
    'AT',
    'GR',
    'PT',
    'HU',
  ],
};

/** 方案名词条 key（静态映射：可被 i18n 扫描发现，且不与展示标签 PRICE_ZONE_LABELS 耦合） */
const ZONE_PLAN_NAME_KEYS: Record<PriceZoneCode, string> = {
  CN: '跨境方案',
  SEA: '东南亚方案',
  LATAM: '拉美方案',
  US: '国际方案',
};

/** 方案名（对齐 perseus planFormat「{{zone}}方案」）：所有位置统一用同一渲染 */
export function zonePlanName(zone: PriceZoneCode, t: (key: string) => string): string {
  return t(ZONE_PLAN_NAME_KEYS[zone]);
}

/**
 * 价区站点范围展示口径（对齐 perseus zoneSiteScopeText）：CN/US 集合过大按「全部站点」摘要；
 * LATAM 用 UED 摘要文案（完整清单由 ⓘ 悬浮展示）；SEA 展开站点清单。
 */
export function zoneSiteScopeText(
  zone: PriceZoneCode,
  t: (key: string) => string,
  getRegionLabel: (region: General.CaselessRegion) => string,
): string {
  if (zone === 'CN' || zone === 'US') return t('全部站点');
  if (zone === 'LATAM') return t('支持东南亚及拉美站点');
  return (ZONE_STORE_REGIONS[zone] ?? [])
    .map((iso) => getRegionLabel(iso as General.CaselessRegion))
    .join('、');
}

/** ⓘ 悬浮的完整站点清单（perseus zoneSitesText 同口径） */
export function zoneSitesListText(
  zone: PriceZoneCode,
  getRegionLabel: (region: General.CaselessRegion) => string,
): string {
  return (ZONE_STORE_REGIONS[zone] ?? [])
    .map((iso) => getRegionLabel(iso as General.CaselessRegion))
    .join('、');
}
